import './ConsoleNavMenu.css';
import 'leaflet/dist/leaflet.css';
import '../console/ConsoleMap.css'
import { useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react'
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger'
import L from "leaflet";
import { ModemData, ModemPositionData, ModemTypeData, ModemTypeListSchema } from '../../api/Drone';
import { APIRequestGet } from '../../utils/APIRequests';
import ConsoleMapMarker, { MarkerData } from './ConsoleMapMarker';
import {
    GPSMapCopterIcon,
    GPSMapCopterOfflineIcon,
    GPSMapPlaneMapIcon,
    GPSMapPlaneOfflineMapIcon
} from '../../assets/icons/GPSMapIcons';
import GoogleMap from '../../maps/GoogleMap';

interface ConsoleMapProps {
    currentDrone: ModemData | null;
    updateFocus: boolean;
    markerClick: (imei: string) => void;
    /** IMEI, прошедшие поиск и фильтры списка: карта показывает ровно их. */
    visibleImeis: string[];
    /** IMEI -> имя устройства, для подписей маркеров. */
    droneNames: Record<string, string>;
    /** Позиции всего парка. Живут в Console: их же использует сетка дронов. */
    positions: ModemPositionData[];
}

// Обзорный вид на случай, когда позиций ещё нет или их нет вовсе. Как только
// приходят координаты своих дронов, карта встаёт по fitBounds и это значение
// больше не используется.
const OVERVIEW_CENTER: L.LatLngExpression = [20, 0];
const OVERVIEW_ZOOM = 2;

// Куда не приближаться сильнее при показе всех.
const FIT_MAX_ZOOM = 15;
const FIT_PADDING: L.PointExpression = [40, 40];

// Масштаб пользователя не трогаем — кроме случая, когда он настолько обзорный,
// что дрон на нём неразличим. Тогда подтягиваем до рабочего.
const MIN_WORK_ZOOM = 13;
const DRONE_ZOOM = 16;

// Доля вьюпорта, внутри которой дрон считается «удобно видимым». Пока он в этой
// зоне, карта не двигается: следование должно держать дрон в кадре, а не
// возить карту за каждым пакетом телеметрии.
const FOLLOW_DEADZONE = 0.6;

// Сколько ждать окончания анимации panTo, прежде чем считать её не доехавшей.
const PAN_GUARD_MS = 1000;

/** Дрон в центральной зоне кадра — двигать карту незачем. */
function isComfortablyVisible(map: L.Map, latlng: L.LatLng): boolean {
    const size = map.getSize();
    if (size.x === 0 || size.y === 0) {
        return false;
    }
    const point = map.latLngToContainerPoint(latlng);
    const marginX = (size.x * (1 - FOLLOW_DEADZONE)) / 2;
    const marginY = (size.y * (1 - FOLLOW_DEADZONE)) / 2;

    return point.x >= marginX && point.x <= size.x - marginX
        && point.y >= marginY && point.y <= size.y - marginY;
}

// У выбранного дрона иконка та же, но с классом для подсветки. Иконки —
// разделяемые синглтоны, поэтому подсвеченные варианты кешируем по исходной.
const activeIcons = new Map<L.Icon, L.Icon>();

function withActiveClass(icon: L.Icon): L.Icon {
    const cached = activeIcons.get(icon);
    if (cached) {
        return cached;
    }
    const active = new L.Icon({ ...icon.options, className: 'console-map-marker-active' });
    activeIcons.set(icon, active);
    return active;
}

function ConsoleMap({ currentDrone, updateFocus, markerClick, visibleImeis, droneNames, positions }: ConsoleMapProps): ReactNode {
    const { t } = useTranslation(['console']);

    // Инстанс карты держим в state, а не в ref: от него зависят эффекты ниже,
    // а присваивание ref не вызывает перерисовку и эффекты бы не запустились.
    const [map, setMap] = useState<L.Map | null>(null);
    // Первый fitBounds делаем один раз: дальше вид принадлежит пользователю.
    const didFitRef = useRef<boolean>(false);
    // Карта тянется за выбранным дроном, пока пользователь не взял вид себе.
    const [following, setFollowing] = useState<boolean>(false);
    // Защёлка на время анимации panTo — см. эффект следования. Хранится сроком,
    // а не флагом: снимается по moveend, но и протухает сама. Иначе одна
    // недоехавшая анимация (вкладка ушла в фон, панорамирование перебито другим
    // setView) оставляла бы защёлку взведённой, и следование молча умирало бы
    // насовсем — проверено, воспроизводится.
    const panGuardRef = useRef<number>(0);
    const [drone, setDrone] = useState<ModemData | null>(currentDrone);
    const [types, setTypes] = useState<ModemTypeData[]>([]);

    const typeMap = useMemo(() => {
        const map = new Map();
        types.forEach(item => {
            map.set(item.imei, item.type);
        });
        return map;
    }, [types]);

    const visibleSet = useMemo(() => new Set(visibleImeis), [visibleImeis]);

    const visiblePositions = useMemo(
        () => positions.filter((p) => visibleSet.has(p.imei)),
        [positions, visibleSet]
    );

    useEffect(() => {
        logger.debug("Console Map mount");

        APIRequestGet('/drone/getModemsType', {
            schema: ModemTypeListSchema,
            logSuccess: true
        }).then((result) => {
            logger.info(result.data);
            setTypes(result.data);
        });
    }, []);

    const fitToVisible = useCallback((): void => {
        if (!map || visiblePositions.length === 0) {
            return;
        }
        // Раскладка могла измениться с прошлого раза (панель дрона, offcanvas,
        // поворот экрана): без пересчёта fitBounds посчитает по старому вьюпорту.
        map.invalidateSize();
        const bounds = L.latLngBounds(visiblePositions.map((p) => L.latLng(p.lat, p.lng)));
        map.fitBounds(bounds, { padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM });
    }, [map, visiblePositions]);

    // Leaflet запоминает размер контейнера в момент создания карты. К этому
    // моменту контейнер ещё не разложен, размер остаётся 0×0 — и дальше Leaflet
    // считает всё для несуществующего вьюпорта: подгружает один тайл вместо
    // двух десятков, а перетаскивание и зум работают по пустым границам.
    // ResizeObserver закрывает и этот случай, и все последующие изменения
    // раскладки: выбор дрона, offcanvas, FPV-режим, поворот экрана.
    useEffect(() => {
        if (!map) {
            return;
        }
        const observer = new ResizeObserver(() => map.invalidateSize());
        observer.observe(map.getContainer());

        return () => observer.disconnect();
    }, [map]);

    // Автофокус на своих дронах при первой загрузке позиций.
    useEffect(() => {
        if (didFitRef.current || visiblePositions.length === 0) {
            return;
        }
        didFitRef.current = true;
        fitToVisible();
    }, [visiblePositions, fitToVisible]);

    useEffect(() => {
        if ((drone?.imei && currentDrone?.imei && (drone.imei !== currentDrone.imei)) || drone === null) {
            setDrone(currentDrone);
        }

        if ((drone?.imei && currentDrone?.imei) || drone === null) {
            const pos = positions.find((item) => currentDrone && (item.imei === currentDrone.imei));
            if (map && pos) {
                const target = L.latLng(pos.lat, pos.lng);

                // Масштаб пользователя сохраняем. Подтягиваем только с обзорных
                // уровней, где дрон — неразличимая точка: раньше здесь стоял
                // Math.max(getZoom(), 16), из-за чего результат зависел от
                // истории просмотра и никогда не отдалял.
                if (map.getZoom() < MIN_WORK_ZOOM) {
                    // Анимацию не включаем: перелёт через несколько уровней
                    // заставляет Leaflet тянуть тайлы на каждом промежуточном.
                    map.setView(target, DRONE_ZOOM, { animate: false });
                } else if (!isComfortablyVisible(map, target)) {
                    // Дрон за кадром или у самого края — подвинем. Если он и так
                    // удобно виден, карту не трогаем.
                    map.panTo(target);
                }
            }
        }
    }, [currentDrone, updateFocus, map]);

    // Следование: пока режим включён, карта подтягивает вид за выбранным
    // дроном — но только когда он выходит из центральной зоны. Иначе вид
    // дёргался бы на каждом пакете позиции.
    useEffect(() => {
        if (!following || !map || !currentDrone) {
            return;
        }
        // Пока идёт анимация панорамирования, координаты на экране считаются по
        // промежуточному положению карты: дрон так и останется «невидимым», и
        // каждый следующий пакет слал бы новый panTo поверх текущего.
        if (Date.now() < panGuardRef.current) {
            return;
        }
        const pos = positions.find((item) => item.imei === currentDrone.imei);
        if (!pos) {
            return;
        }
        const target = L.latLng(pos.lat, pos.lng);
        if (!isComfortablyVisible(map, target)) {
            panGuardRef.current = Date.now() + PAN_GUARD_MS;
            map.panTo(target);
        }
    }, [following, map, currentDrone, positions]);

    useEffect(() => {
        if (!map) {
            return;
        }
        const done = (): void => { panGuardRef.current = 0; };
        map.on('moveend', done);

        return () => {
            map.off('moveend', done);
        };
    }, [map]);

    // «Показать все» и следование — взаимоисключающие намерения: пока режим
    // включён, вид вернулся бы к дрону на первом же пакете, и кнопка отменяла
    // бы сама себя.
    const showAll = useCallback((): void => {
        setFollowing(false);
        fitToVisible();
    }, [fitToVisible]);

    // Выбрали дрон — включаем следование, сняли выбор — выключаем.
    useEffect(() => {
        setFollowing(currentDrone != null);
    }, [currentDrone]);

    // Пользователь потащил карту — значит, смотрит сам. Зум режим не сбрасывает:
    // приблизиться к дрону, продолжая за ним следить, — нормальный сценарий.
    useEffect(() => {
        if (!map) {
            return;
        }
        const stopFollowing = (): void => setFollowing(false);
        map.on('dragstart', stopFollowing);

        return () => {
            map.off('dragstart', stopFollowing);
        };
    }, [map]);


    const handleMarkerClick = useCallback((marker: MarkerData): void => {
        markerClick(marker.imei);
    }, [markerClick]);

    const getIcon = (imei: string, isOnline: boolean): L.Icon => {
        const type = typeMap.get(imei);
        if (type === 1) return isOnline ? GPSMapPlaneMapIcon : GPSMapPlaneOfflineMapIcon;
        return isOnline ? GPSMapCopterIcon : GPSMapCopterOfflineIcon;
    };

    const renderedMarkers = useMemo(
        () => {
            return visiblePositions.map((marker) => {
                const isSelected = currentDrone?.imei === marker.imei;
                const icon = getIcon(marker.imei, marker.isOnline);
                return (
                    <ConsoleMapMarker
                        key={marker.imei}
                        marker={marker}
                        icon={isSelected ? withActiveClass(icon) : icon}
                        label={droneNames[marker.imei] ?? marker.imei}
                        isSelected={isSelected}
                        onClick={handleMarkerClick}
                        isOnline={marker.isOnline}
                    />
                );
            })
        },
        [visiblePositions, currentDrone, types, droneNames, handleMarkerClick]
    );

    return (
        <div className='console-map-container'>
            <div className={"map google-map"} style={{ width: "100%", height: "100%" }}>
                <GoogleMap
                    center={OVERVIEW_CENTER}
                    zoom={OVERVIEW_ZOOM}
                    whenCreated={setMap}
                >
                    {renderedMarkers}
                </GoogleMap>
            </div>
            <div className='console-map-controls'>
                {/* Тумблер виден только при выбранном дроне: следовать не за кем. */}
                {currentDrone &&
                    <button
                        type='button'
                        className={'console-map-control' + (following ? ' console-map-control-on' : '')}
                        aria-pressed={following}
                        onClick={() => setFollowing((on) => !on)}
                    >
                        {following ? t('map_following_on') : t('map_following_off')}
                    </button>
                }
                {visiblePositions.length > 0 &&
                    <button
                        type='button'
                        className='console-map-control'
                        onClick={showAll}
                    >
                        {t('map_fit_all')}
                    </button>
                }
            </div>
        </div>
    )
}

export default ConsoleMap;
